import { fromUTCMidnightSeconds, currentDayStartInUTC, parseVisibilityTime } from 'utils/time'
import { PROPERTIES } from 'routes/Studies/routes/Study/routes/Instruments/utils/PropertyFields'
import moment from 'moment'
import { _isObject } from 'utils/object'
import {
  HORIZONTAL_SELECT_ONE_MAX_CHOICES,
  HORIZONTAL_SELECT_ONE_MIN_CHOICES,
  CONSENT_SIGNEE_MAP,
  DEPLOYMENT_KEYS,
  GROUP_TYPE_MAP,
  INSTRUMENT_ERROR_LIST,
  INSTRUMENT_TYPE_MAP,
  INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM,
  isSchedulePlatformTypeValid,
  QUESTION_TYPE_MAP,
  TRIGGER_TYPE_MAP,
  urlRegex,
} from 'utils/constants'
import STRINGS from 'utils/strings'
import { collectDeployVisits, getChainDeploymentStudyScheduleIds } from 'utils/chainDeploymentUtils'
import { collectExpireVisits, getArmIdForVisitScheduleId, getArmsMissingExpireVisits } from 'utils/chainExpiryUtils'
import { stripHtmlTags } from 'utils/misc'
import {
  resolveScheduleVisibility,
  usesPerScheduleVisibility,
} from 'routes/Studies/routes/Study/routes/Instruments/utils/scheduleVisibility'

import { omitNonBlockingTmgSaveErrors, validateTimedMatchingGridQuestion } from './timedMatchingGrid'
import {
  getDoesFormulaContainInvalidChars,
  getIsFormulaIncomplete,
} from 'routes/Studies/routes/Study/routes/Instruments/routes/Instrument/routes/Scoring/utils/scoring'

import {
  isRepeatTimeUnitOpt,
  validateRecurringFields,
} from '../routes/Studies/routes/Study/routes/Instruments/routes/Instrument/routes/Scheduler/constants/schedulerConstants'

const NEVER = DEPLOYMENT_KEYS.never
const NOW = DEPLOYMENT_KEYS.now
const RELATIVE_TO_FIRST_LOGIN = DEPLOYMENT_KEYS.firstLogin
const RELATIVE_TO_SEND_IDEAL = DEPLOYMENT_KEYS.relativeToSendIdeal

export const checkIsInvalidSignee = (signeeType, siteRolesStatus) => {
  const { witness_status: witnessStatus, clinician_status: clinicianStatus } = siteRolesStatus
  let hasError = false
  if (signeeType === CONSENT_SIGNEE_MAP.witness && !witnessStatus) {
    hasError = true
  }
  if (signeeType === CONSENT_SIGNEE_MAP.clinro && !clinicianStatus) {
    hasError = true
  }
  return hasError
}

//
// Question Item Errors
//
export function getQuestionErrors({
  question,
  isSurveyOrDiary,
  siteRolesStatus,
  isEditConsent,
  /** When false, instrument review screen is off — skip review-only rules (matches Survey toolbar). */
  reviewScreenEnabled = true,
  /** Review screen text is available only for V2 studies. */
  isV2Study = false,
}) {
  // check label
  const errors = {}

  if (
    question.attributes?.hasScoring &&
    question.type !== QUESTION_TYPE_MAP.timedMatchingGrid &&
    question.choices_order?.some(id => question.choices[id].score_value === '')
  ) {
    errors.blankScoreErr = 'Score values cannot be blank'
  }
  if (
    question.type === QUESTION_TYPE_MAP.numericRatingScale &&
    question.attributes?.hasOptOut &&
    !question.attributes?.opt_out
  ) {
    errors.blankOptOutErr = 'Opt out value cannot be blank'
  }
  if (question.type === QUESTION_TYPE_MAP.foodEntry && question.food_data_review) {
    const foodDataFieldsValues = Object.values(question.food_data_fields)
    const selectedValues = foodDataFieldsValues.filter(value => value)
    if (!selectedValues.length) {
      errors.blankFoodDataFields = 'Food data fields cannot be blank'
    }
  }
  if (question.type === QUESTION_TYPE_MAP.integratedDevice) {
    if (!question.attributes.integrated_device_fields.device_type.length) {
      errors.blankIntegratedDeviceField = 'Integrated Device fields cannot be blank'
    }
    const deviceFields = question.attributes.integrated_device_fields
    if (deviceFields.device_type === 'mir_smart') {
      const maxBlows = deviceFields.max_blows_allowed
      const targetBlows = deviceFields.target_good_blows
      if (!maxBlows || maxBlows < 1 || maxBlows > 100) {
        errors.mirSmartMaxBlowsError = 'Max Blows Allowed must be between 1 and 100'
      }
      if (!targetBlows || targetBlows < 1) {
        errors.mirSmartTargetBlowsError = 'Target Good Blows must be at least 1'
      } else if (maxBlows && targetBlows > maxBlows) {
        errors.mirSmartTargetBlowsError = 'Target Good Blows must not exceed Max Blows Allowed'
      }
    }
    if (deviceFields.device_type === 'roche_accu_chek' && question.attributes?.required) {
      if (
        deviceFields.has_on_demand_question_text &&
        !stripHtmlTags(deviceFields.on_demand_question_text || '').trim()
      ) {
        errors.blankOnDemandQuestionTextErr = STRINGS.integratedDeviceOnDemandQuestionTextRequiredErr
      }
      if (
        deviceFields.has_prefilled_question_text &&
        !stripHtmlTags(deviceFields.prefilled_question_text || '').trim()
      ) {
        errors.blankPrefilledQuestionTextErr = STRINGS.integratedDevicePrefilledQuestionTextRequiredErr
      }
    }
  }
  const isAccuChekIntegratedDeviceWithCustomQuestionText =
    question.type === QUESTION_TYPE_MAP.integratedDevice &&
    question.attributes?.integrated_device_fields?.device_type === 'roche_accu_chek' &&
    (!!question.attributes?.integrated_device_fields?.has_on_demand_question_text ||
      !!question.attributes?.integrated_device_fields?.has_prefilled_question_text)
  if (
    question.type !== QUESTION_TYPE_MAP.signature &&
    question.label === '' &&
    question.type !== QUESTION_TYPE_MAP.horizontalSelectOne &&
    !isAccuChekIntegratedDeviceWithCustomQuestionText
  ) {
    errors.blankLabelErr = 'Question labels cannot be blank'
  }
  if (question.type === QUESTION_TYPE_MAP.signature) {
    if (getSigneeRequiredCount(question.attributes.signees) < 1) {
      errors.atLeastOneSigneeRequiredErr = STRINGS.requiredSigneeErr
    }
    if (isEditConsent) {
      const signeeArr = Object.values(question.attributes.signees)
      for (let i = 0; i < signeeArr.length; i++) {
        const signee = signeeArr[i]
        const hasInvalidSignee = checkIsInvalidSignee(signee.type, siteRolesStatus)
        if (hasInvalidSignee) {
          errors.invalidSigneesError = STRINGS.invalidSigneePrompt
          break
        }
      }
    }
  }
  if (question.type === QUESTION_TYPE_MAP.pdf && isEditConsent) {
    if (!question.url) {
      errors.noPdfUpload = STRINGS.questionErrorNoPdf
    }
  }

  if ([QUESTION_TYPE_MAP.selectOne, QUESTION_TYPE_MAP.selectMultiple].includes(question.type)) {
    // check blank choices
    if (question.choices_order.some(id => !question.choices[id].label) || question.attributes.other === '') {
      errors.blankChoicesErr = 'Choices cannot be blank'
    }

    if (isSurveyOrDiary) {
      if ([QUESTION_TYPE_MAP.selectOne, QUESTION_TYPE_MAP.selectMultiple].includes(question.type)) {
        if (question.choices_order.some(id => question.choices[id].label === '')) {
          errors.blankChoicesErr = 'Choices cannot be blank'
        }
      } else if (question.choices_order.some(id => !question.choices[id].label) || question.attributes.other === '') {
        errors.blankChoicesErr = 'Choices cannot be blank'
      }
    }

    // check number of choices
    const numChoices = question.choices_order.length + (question.attributes.other ? 1 : 0)

    if (numChoices < 2) {
      errors.minChoicesErr = 'Must have at least two choices.'
    }
  }

  if (question.type === QUESTION_TYPE_MAP.longListSelectOne) {
    if (question.choices_order.some(id => !question.choices[id].label)) {
      errors.blankChoicesErr = 'Choices cannot be blank'
    }
    if (question.choices_order.length < 10) {
      errors.minChoices = 'Select One question type is recommended for questions with fewer than 10 answer choices'
    }
  }

  if ([QUESTION_TYPE_MAP.likert].includes(question.type)) {
    if (question.choices_order.length === 5) {
      if (question.choices_order.some(id => !question.choices[id].label) || question.attributes.other === '') {
        errors.blankChoicesErr = 'Choices cannot be blank'
      }
    } else {
      const [choiceOne, choiceSeven] = [question.choices_order[0], question.choices_order[6]]
      if (!question.choices[choiceOne].label || !question.choices[choiceSeven].label) {
        errors.blankChoicesErr = 'Choices cannot be blank'
      }
    }
  }

  if ([QUESTION_TYPE_MAP.numericRatingScale].includes(question.type)) {
    if (question.choices_order.some(id => !question.choices[id].value && question.choices[id].value !== 0)) {
      errors.blankValuesErr = 'Values cannot be blank'
    }
    if (question.choices_order.some(id => question.choices[id].value < 0 || question.choices[id].value >= 100)) {
      errors.valuesErr = 'Values cannot be less than 0 or greater than 99'
    }
  }

  if ([QUESTION_TYPE_MAP.horizontalSelectOne].includes(question.type)) {
    const nChoices = question.choices_order.length
    if (
      nChoices < HORIZONTAL_SELECT_ONE_MIN_CHOICES ||
      nChoices > HORIZONTAL_SELECT_ONE_MAX_CHOICES
    ) {
      errors.horizontalSelectOneChoicesErr = `Horizontal Select One must have between ${HORIZONTAL_SELECT_ONE_MIN_CHOICES} and ${HORIZONTAL_SELECT_ONE_MAX_CHOICES} options`
    }
    if (
      question.choices_order.some(id => {
        const raw = question.choices[id].value
        return raw === '' || raw === undefined || raw === null || String(raw).trim() === ''
      })
    ) {
      errors.horizontalSelectOneValuesErr = 'Each option value cannot be blank'
    }
    const reviewTextApplies =
      isV2Study && reviewScreenEnabled !== false && question.attributes?.in_review !== false
    if (
      reviewTextApplies &&
      question.attributes?.has_review_screen_text &&
      question.attributes?.required &&
      !stripHtmlTags(question.attributes?.review_screen_text || '').trim()
    ) {
      errors.blankReviewScreenTextErr = STRINGS.surveyReviewScreenTextRequiredErr
    }
  }

  if ([QUESTION_TYPE_MAP.matrix].includes(question.type)) {
    if ((question.choices_order || []).some(id => !question.choices?.[id]?.label)) {
      errors.blankChoicesErr = 'Choices cannot be blank'
    }
    if ((question.questions_order || []).some(id => question.questions?.[id]?.label === '')) {
      errors.blankQuestionsErr = 'Questions cannot be blank'
    }
  }

  if (question.type === QUESTION_TYPE_MAP.audioRecording) {
    const { attributes } = question
    const { min, max } = attributes

    if ((!min && min !== 0) || (max === '' && max !== 0)) errors.blankDurationErr = 'Enter a valid number'
    if ((!!min && min < 0) || min === 0 || (!!max && max < 0) || max === 0)
      errors.minDurationErr = 'Cannot be less than 1 second'
    if (min > 900 || max > 900) errors.maxDurationErr = 'Cannot exceed 900 seconds (15 minutes)'
    if (min >= max) errors.minExceedMaxErr = 'Maximum must be larger than minimum'
  }

  if (question.type === QUESTION_TYPE_MAP.timer) {
    const { attributes } = question
    const { timer_details = {} } = attributes
    const { time_string } = timer_details

    if (!time_string || time_string === '00:00') {
      errors.zeroMinutesAndSeconds = 'Time cannot be blank'
    }
  }

  if (question.type === QUESTION_TYPE_MAP.timedMatchingGrid) {
    Object.assign(errors, omitNonBlockingTmgSaveErrors(validateTimedMatchingGridQuestion(question)))
  }

  if (question.type === QUESTION_TYPE_MAP.multipleField) {
    if (
      question.choices_order.some(id => {
        const { min, max } = question.choices[id]
        return !!max && !!min && min >= max
      })
    ) {
      errors.minMax = 'Minimum value must not exceed maximum value.'
    }
    if (
      question.choices_order.some(id => {
        const { min, max } = question.choices[id]
        return max < 0 || min < 0
      })
    ) {
      errors.negVal = 'Values cannot be negative.'
    }
  }

  // check for logic errors
  const logicError = getLogicErrors(question.logic.show_if)

  if (logicError.length > 0) {
    errors.logicErr = logicError
  }

  if (question.type === QUESTION_TYPE_MAP.vaScale || question.type === QUESTION_TYPE_MAP.vaScale2021) {
    if (question.scale_labels?.top === '' || question.scale_labels?.bottom === '' || !question.scale_labels) {
      errors.VASLabelError = 'Scale labels cannot be blank'
    }
  }

  // check for input range errors
  if (question.input_validation) {
    const { max, min, absolute_time } = question.input_validation
    const keysExist = max !== '' && min !== ''
    if (keysExist && min > max) {
      errors.numericInputRangeError = 'First field cannot be greater than the second'
    }

    if (absolute_time) {
      const { max: _max, min: _min } = absolute_time
      const format = 'HH:mm'
      if (!!_max && !!_min) {
        const maxMom = moment(_max, format)
        const minMom = moment(_min, format)
        const maxIsBeforeMin = maxMom.isBefore(minMom)
        if (maxIsBeforeMin) {
          errors.timeInputRangeError = 'First time must precede the second'
        }
      }
    }
  }

  return errors
}

function getLogicErrors(logic, errors = [], groupNumber = 0) {
  if (!logic) return errors
  const oppositeOps = {
    is_answered: 'is_not_answered',
    is_not_answered: 'is_answered',
    is_selected: 'is_not_selected',
    is_not_selected: 'is_selected',
    '>': '<',
    '<': '>',
    '>=': '<',
    '<=': '>',
  }
  const emptyMessage = 'All logic fields must be filled.'
  const tracker = {}
  for (let i = 0; i < logic.terms.length; i++) {
    const condition = logic.terms[i]
    tracker[condition.question_id] = tracker[condition.question_id] || {}
    tracker[condition.question_id][condition.comparator] = { value: condition.value, logicNumber: i + 1 }
    if (condition.comparator === 'boolean_combine') {
      groupNumber++
      getLogicErrors(condition, errors, groupNumber)
    }
    // add blank field logic error
    if (!errors.includes(emptyMessage) && _hasEmptyLogicField(condition)) {
      errors.push(emptyMessage)
    } else if (logic.operator === 'and') {
      // add conflicting logic errors if there are conflicting logic conditions
      const prevCondition = tracker[condition.question_id]
      const oppositeOp = oppositeOps[condition.comparator]
      if (prevCondition && prevCondition[oppositeOp] && prevCondition[oppositeOp].value === condition.value) {
        errors.push(
          `${groupNumber !== 0 ? `Group ${groupNumber}: ` : ''}Logic condition ${prevCondition[oppositeOp].logicNumber
          } conflicts with condition ${i + 1}`,
        )
      }
    }
  }
  return errors
}

// URL ERROR HANDLING

export const getArticleErrors = article => {
  const errors = {}
  const { url, title } = article

  if (title === '') errors.titleErr = 'Article title cannot be empty'
  if (!url) errors.blankURLError = 'Article URL cannot be empty'
  else if (url.includes(' ')) errors.URLError = 'Article URL should not have spaces.'
  else if (!url.match(urlRegex)) errors.invalidUrlError = 'Please enter a valid URL.'
  return errors
}

// SURVEY CONSENT HANDLING

export const getCreateNewConsentErrors = consent => {
  const errors = {}
  const { consent_title: consentName, metadata, consent_type: consentType } = consent
  if (consentType === 'addendum') return errors
  const { display_name: displayName } = metadata
  if (!consentName || consentName === '') errors.consentName = 'Consent Name cannot be empty'
  if (!displayName || displayName === '') errors.displayName = 'Display Name cannot be empty'
  return errors
}

export function getConsentErrors({
  consent,
  skipQuestionValidation = false,
  ignoreScheduling = false,
  siteRolesStatus = {},
  isEditConsent = false,
}) {
  if (skipQuestionValidation) {
    return getCreateNewConsentErrors(consent)
  }
  const errors = { questions: {} }
  let hasUnskippableSignatureQuestion

  Object.keys(consent.questions).forEach(key => {
    const qErrors = getQuestionErrors({
      question: consent.questions[key],
      siteRolesStatus,
      isEditConsent,
    })
    if (Object.keys(qErrors).length > 0) errors.questions[key] = qErrors

    if (consent.questions[key].type === 'signature' && Object.keys(consent.questions[key].logic).length === 0) {
      hasUnskippableSignatureQuestion = true
    }
  })

  if (!hasUnskippableSignatureQuestion) {
    errors.noUnskippableSignatureErr =
      'There must be a signature question type in this form. Remove skip logic on this question type or add a signature question without skip logic.'
  }

  if (ignoreScheduling) return errors
  const schedulingErrors = getSchedulingErrors(consent)
  return { ...errors, ...schedulingErrors }
}

// SURVEY ERROR HANDLING

/**
 * @param {object} [options]
 * @param {boolean} [options.reviewScreenEnabled] — from instrument metadata when editor state is authoritative (toolbar toggle).
 */
export function getSurveyErrors(survey, options = {}) {
  const errors = { questions: {} }
  const reviewScreenEnabled =
    options.reviewScreenEnabled !== undefined && options.reviewScreenEnabled !== null
      ? options.reviewScreenEnabled
      : survey.metadata?.review_screen?.enabled
  const isV2Study = !!options.isV2Study
  if (Object.keys(survey.questions).length < 1) {
    errors.noQuestionsErr = 'Survey must have at least one question.'
  }
  if (!survey.title) {
    errors.titleErr = 'Survey must have a title.'
  }
  Object.keys(survey.questions).forEach(key => {
    const qErrors = getQuestionErrors({
      question: survey.questions[key],
      isSurveyOrDiary: true,
      reviewScreenEnabled,
      isV2Study,
    })
    if (Object.keys(qErrors).length > 0) errors.questions[key] = qErrors
  })
  return errors
}
export function getClinroErrors(clinro) {
  const errors = { questions: {} }
  const reviewScreenEnabled = clinro.metadata?.review_screen?.enabled
  if (Object.keys(clinro.questions).length < 1) {
    errors.noQuestionsErr = 'Instrument must have at least one question.'
  }
  if (!clinro.title) {
    errors.titleErr = 'Instrument must have a title.'
  }
  Object.keys(clinro.questions).forEach(key => {
    const qErrors = getQuestionErrors({
      question: clinro.questions[key],
      reviewScreenEnabled,
    })
    if (Object.keys(qErrors).length > 0) errors.questions[key] = qErrors
  })
  return errors
}
export function getDiaryErrors(diary) {
  const errors = { questions: {} }
  const reviewScreenEnabled = diary.metadata?.review_screen?.enabled
  if (Object.keys(diary.questions).length < 1) {
    errors.noQuestionsErr = 'Diary must have at least one question.'
  }
  if (!diary.title) {
    errors.titleErr = 'Diary must have a title.'
  }
  Object.keys(diary.questions).forEach(key => {
    const qErrors = getQuestionErrors({
      question: diary.questions[key],
      isSurveyOrDiary: true,
      reviewScreenEnabled,
    })
    if (Object.keys(qErrors).length > 0) errors.questions[key] = qErrors
  })
  return errors
}

function _hasEmptyLogicField(condition) {
  return (
    (condition.value === '' && !condition.comparator.includes('answered')) ||
    condition.question_id === '' ||
    condition.comparator === '' ||
    condition.choice_id === ''
  )
}

/**
 * Checks that the metadata (minus scheduling) values are valid.
 *  - title cannot be blank
 *  - All metadata UI fields must be present
 * @param metadata
 * @returns {Object} errors
 */

// METADATA AND SCHEDULING

export const getAllErrors = instrument => {
  return { ...getSchedulingErrors(instrument), ...getMetadataErrors(instrument) }
}

export const getMetadataErrors = (instrument, isNewInstArch = false, isInstrumentUnificationEnabled = false) => {
  const { metadata, title } = instrument
  const errors = {}
  if (_isInvalid(title)) errors.title = 'Required field'
  const keys = Object.keys(PROPERTIES).filter(p =>
    isNewInstArch && isInstrumentUnificationEnabled ? p : p !== 'display_location',
  )

  keys.forEach(key => {
    if (key === 'display_icon' && instrument.type === 'TASK') return
    if (PROPERTIES[key].required && _isInvalid(metadata[key])) {
      errors[key] = 'Required field'
    }
    if (key === 'display_location' && !metadata[key]?.length) {
      errors[key] = 'Required field'
    }
  })

  return errors
}

export const _isInvalid = (value, nullCheck = true) => {
  const conditions = ['', undefined]
  if (nullCheck) conditions.push(null)
  return conditions.some(falsy => value === falsy) || value !== value
}
/**
 * Deployment Validations
 * 1. deploy.first_login.interval cannot be less than expire.first_login.interval
 * 2. If recurring.recurrences is set, expire must be set to { never: null }
 * 3. If deploy.first_login is set, expire cannot be set to absolute
 * 4. recurring.interval must be less than expire.first_login.interval - deploy.first_login.interval
 * 5. recurring.interval cannot exceed absolute expiration date (calculated from deploy time)
 * 6. visibility and visibilityRanges must always be defined and
 *    visibilityRanges must be set to default null state (see above) when visibility === 'all_day'
 */

export const getSchedulingErrors = (instrument, conditions, studyVisitSchedule = [], options = {}) => {
  if (!instrument.metadata.hasOwnProperty('schedule')) return { schedule: 'Schedule property is missing' }
  const errors = {}
  const handleError = (key, text, index) => {
    if (index || index === 0) {
      errors[key] = text
    } else errors[key] = text
  }

  const { hasMultiSchedule = false, confirmedSchedules = [] } = options
  const metadata = instrument.metadata
  const schedules = confirmedSchedules?.length
    ? confirmedSchedules
    : Array.isArray(metadata.schedule)
      ? metadata.schedule
      : metadata.schedule
        ? [metadata.schedule]
        : []
  const perScheduleVisibility = usesPerScheduleVisibility(
    hasMultiSchedule,
    schedules,
    metadata.instrument_reuse,
  )

  // ClinRO instruments must specify the allowed clinician role level(s).
  if (instrument?.type === INSTRUMENT_TYPE_MAP.clinro && (!instrument?.instrument_roles || instrument.instrument_roles.length === 0)) {
    handleError('instrument_roles', 'Clinician level is required')
  }

  // GOLD-11299 / GOLD-12204: Validate visibility on schedule or metadata depending on instrument shape
  if (perScheduleVisibility) {
    const editingSchedule = Array.isArray(metadata.schedule) ? metadata.schedule[0] : metadata.schedule
    _getVisibilityErrors(resolveScheduleVisibility(editingSchedule, metadata), handleError)
  } else {
    _getVisibilityErrors(metadata, handleError)
  }

  if (conditions?.length > 0) {
    _getConditionErrors(conditions, handleError)
  }
  _getSchedulingErrors(instrument.metadata.schedule, handleError)
  _getDeployErrors(instrument.metadata.schedule, handleError)
  _getExpireErrors(instrument.metadata.schedule, handleError)
  _getCohortErrors(instrument.metadata.schedule, handleError)
  _getRecurringErrors(instrument.metadata.schedule, handleError)
  _getChainDeploymentErrors(instrument.metadata.schedule, studyVisitSchedule, handleError)
  _getChainExpiryErrors(instrument.metadata.schedule, studyVisitSchedule, handleError)

  if (INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM.includes(instrument.type)) {
    if (!isSchedulePlatformTypeValid(instrument.metadata.schedule?.platform_type)) {
      handleError(
        'schedule.platform_type',
        'Platform must be mobile, web, or all.',
      )
    }
  }

  // Check for prevalidated errors (prevalErrors) from the reducer
  // These are errors set by components like InstrumentExpireVisitSelection
  // This should be checked FIRST as it's the most reliable indicator of validation state
  // If a key exists in prevalErrors, it indicates a validation error (even if value is empty string)
  const { prevalErrors } = instrument.metadata.schedule
  if (prevalErrors && typeof prevalErrors === 'object') {
    Object.keys(prevalErrors).forEach(errorKey => {
      const errorValue = prevalErrors[errorKey]
      // If the key exists in prevalErrors, it means there's a validation error
      // Empty string '' is a valid error indicator (component sets it that way)
      if (errorKey === 'noVisit' || errorKey === 'noVisits') {
        // Always set error if key exists, use default message if value is empty/falsy
        errors['schedule.chain_expiry_info.visit_ids'] = errorValue || 'Please select a visit for expiration'
      } else if (errorKey === 'chain_deployment_info.visit_ids') {
        errors['schedule.chain_deployment_info.visit_ids'] = errorValue || 'Please select a visit'
      } else if (errorKey === 'invalidInstrumentTrigger') {
        errors['schedule.chain_deployment_info.instrument_id'] = errorValue || 'Invalid instrument trigger'
      } else {
        // For other errors, use the key as-is
        errors[`schedule.${errorKey}`] = errorValue || 'Validation error'
      }
    })
  }

  // Validate chain_expiry_info.visit_ids when expire_on_visit_confirmation is true
  // This is a fallback validation if prevalErrors is not set
  const { expire, chain_expiry_info, chain_deployment_info } = instrument.metadata.schedule
  if (expire?.expire_on_visit_confirmation && chain_expiry_info?.chain_expiry_by_ss_visit) {
    const visitIds = chain_expiry_info.visit_ids
    const terms = chain_expiry_info?.expiry_condition?.terms
    const hasVisits =
      (Array.isArray(visitIds) && visitIds.length > 0) ||
      (Array.isArray(terms) && terms.length > 0) ||
      chain_expiry_info?.visit_id ||
      (Array.isArray(chain_expiry_info?.visits) && chain_expiry_info.visits.length > 0)

    // Only add error if it's not already set by prevalErrors
    if (!hasVisits && !errors['schedule.chain_expiry_info.visit_ids']) {
      errors['schedule.chain_expiry_info.visit_ids'] = 'Please select a visit for expiration'
    }

    const deployArmIds = getChainDeploymentStudyScheduleIds(chain_deployment_info)
    if (deployArmIds.length > 1) {
      const missingArmVisits = getArmsMissingExpireVisits(
        deployArmIds,
        collectExpireVisits(chain_expiry_info),
        visit => getArmIdForVisitScheduleId(studyVisitSchedule, visit.visit_schedule_id),
      )
      if (missingArmVisits.length > 0 && !errors['schedule.chain_expiry_info.visit_ids']) {
        errors['schedule.chain_expiry_info.visit_ids'] = STRINGS.pleaseSelVisitFromEachArm
      }
    }
  }

  if (chain_deployment_info?.chain_deployment_by_ss_visit) {
    const deployArmIds = getChainDeploymentStudyScheduleIds(chain_deployment_info)
    const deployVisits = collectDeployVisits(chain_deployment_info)
    const hasDeployVisits = deployVisits.length > 0

    if (!hasDeployVisits && !errors['schedule.chain_deployment_info.visit_ids']) {
      errors['schedule.chain_deployment_info.visit_ids'] = 'Please select a visit'
    }

    if (deployArmIds.length > 1 && hasDeployVisits) {
      const missingArmVisits = getArmsMissingExpireVisits(
        deployArmIds,
        deployVisits,
        visit => getArmIdForVisitScheduleId(studyVisitSchedule, visit.visit_schedule_id),
      )
      if (missingArmVisits.length > 0 && !errors['schedule.chain_deployment_info.visit_ids']) {
        errors['schedule.chain_deployment_info.visit_ids'] = STRINGS.pleaseSelVisitFromEachArm
      }
    }
  }

  return errors
}

const shouldSkipRecursiveScheduleRoute = route =>
  route === 'schedule.visibilityRanges' ||
  route.startsWith('schedule.visibilityRanges.') ||
  route === 'schedule.prevalErrors' ||
  route.startsWith('schedule.prevalErrors.')

const isMomentOrDateValue = value => moment.isMoment(value) || value instanceof Date

export const _getSchedulingErrors = (schedule, handleError, route = 'schedule') => {
  // GOLD-12318: visibilityRanges are stored as moment objects after load. Walking
  // those objects treats internals like `_pf.meridiem` as empty required fields.
  if (shouldSkipRecursiveScheduleRoute(route) || isMomentOrDateValue(schedule)) {
    return
  }
  if (!_isObject(schedule)) {
    if (_isInvalid(schedule, false)) {
      let errorMessage = 'This field cannot be empty or has invalid input'
      if (route.includes('cohort.filter.instrument_score')) {
        errorMessage = 'Score cannot be empty'
        handleError(INSTRUMENT_ERROR_LIST.instrumentScore, errorMessage)
        return
      }
      // Multi-visit / multi-arm payloads intentionally leave legacy singular fields empty.
      // Dedicated validators below handle required visit/arm selection.
      if (
        route === 'schedule.chain_deployment_info.visit_id' ||
        route === 'schedule.chain_deployment_info.visit_ids' ||
        route === 'schedule.chain_deployment_info.visit_schedule_id' ||
        route === 'schedule.chain_deployment_info.visit_schedule_ids' ||
        route === 'schedule.chain_deployment_info.study_schedule_id' ||
        route === 'schedule.chain_deployment_info.study_schedule_ids' ||
        route === 'schedule.chain_expiry_info.visit_id' ||
        route === 'schedule.chain_expiry_info.visit_ids' ||
        route === 'schedule.chain_expiry_info.visit_schedule_id' ||
        route === 'schedule.chain_expiry_info.study_schedule_id' ||
        route === 'schedule.chain_expiry_info.study_schedule_ids'
      ) {
        return
      }
      handleError(route, errorMessage)
    }
  } else {
    const keys = Object.keys(schedule)
    keys.forEach(key => {
      _getSchedulingErrors(schedule[key], handleError, `${route}.${key}`)
    })

    // Add specific validation for chain_expiry_info terms
    if (route === 'schedule.chain_expiry_info.expiry_condition' && schedule.terms) {
      const invalidTerms = schedule.terms.filter(term => {
        // If days_offset is defined, both days_offset and timeRelated must be valid
        if (term.days_offset !== undefined) {
          return (
            term.days_offset === undefined ||
            term.days_offset === null ||
            term.timeRelated === undefined ||
            term.timeRelated === null
          )
        }
        return false
      })

      if (invalidTerms.length > 0) {
        handleError(
          `${route}.terms`,
          'For visits with expire offset enabled, both days offset and time relation must be specified',
        )
      }

      // Also validate that days_offset is a number if defined
      schedule.terms.forEach((term, index) => {
        if (term.days_offset !== undefined && typeof term.days_offset !== 'number') {
          handleError(`${route}.terms[${index}].days_offset`, 'Days offset must be a number')
        }
      })
    }
  }
}

/**
 * GOLD-11299: Validate visibility and visibilityRanges for invalid date values
 * This catches "Invalid date" strings that may come from uploaded JSONs
 */
const _getVisibilityErrors = (metadata, handleError) => {
  const { visibility, visibilityRanges } = metadata

  if (visibility === 'specific_time') {
    if (!visibilityRanges || !Array.isArray(visibilityRanges) || visibilityRanges.length === 0) {
      handleError('visibilityRanges', 'Visibility time ranges are required when using specific times')
      return
    }

    visibilityRanges.forEach((range, index) => {
      if (!range) {
        handleError(`visibilityRanges[${index}]`, 'Invalid visibility range')
        return
      }

      const { start, end } = range

      if (start === 'Invalid date' || end === 'Invalid date') {
        handleError('visibilityRanges', 'Visibility time range contains invalid dates. Please set valid start and end times.')
        return
      }

      if (start !== null && !parseVisibilityTime(start)) {
        handleError('visibilityRanges', 'Visibility start time is invalid. Please select a valid time.')
        return
      }

      if (end !== null && !parseVisibilityTime(end)) {
        handleError('visibilityRanges', 'Visibility end time is invalid. Please select a valid time.')
        return
      }

      if (start === null || end === null) {
        handleError('visibilityRanges', 'Both start and end times are required for specific visibility times')
      }
    })
  }
}

/**
 * GOLD-11299: Validate chain deployment info for invalid arm/visit IDs
 * When instrument is configured to deploy by visit, the arm and visit must exist in the study
 */
const _getChainDeploymentErrors = (schedule, studyVisitSchedule, handleError) => {
  const { chain_deployment_info } = schedule
  if (!chain_deployment_info || !studyVisitSchedule || studyVisitSchedule.length === 0) return

  const { visit_schedule_id, visit_id, chain_deployment_by_ss_visit } = chain_deployment_info

  if (!chain_deployment_by_ss_visit) return

  const armIds = getChainDeploymentStudyScheduleIds(chain_deployment_info)

  if (armIds.length === 0) {
    handleError(
      'schedule.chain_deployment_info.study_schedule_id',
      'Please select at least one arm for deployment.',
    )
    return
  }

  const invalidArmIds = armIds.filter(armId => !studyVisitSchedule.some(arm => arm.id === armId))
  if (invalidArmIds.length > 0) {
    handleError(
      'schedule.chain_deployment_info.study_schedule_id',
      'The selected arm does not exist in this study. Please select a valid arm for deployment.',
    )
    return
  }

  const visitIdsToValidate =
    Array.isArray(chain_deployment_info.visit_ids) && chain_deployment_info.visit_ids.length > 0
      ? chain_deployment_info.visit_ids
      : visit_id
        ? [visit_id]
        : []

  if (visitIdsToValidate.length > 0) {
    const allVisits = armIds.flatMap(armId => {
      const arm = studyVisitSchedule.find(item => item.id === armId)
      if (!arm?.phases) return []

      return arm.phases.flatMap(phase => {
        const fromTemplates = phase.visit_templates || []
        const fromCycles = (phase.cycles || []).flatMap(cycle =>
          (cycle.visits || []).map(visit => ({
            ...visit,
            visit_schedule_id: visit.visit_schedule_id || cycle.id,
          })),
        )
        return [...fromTemplates, ...fromCycles]
      })
    })

    const validVisitIds = visitIdsToValidate.filter(id =>
      allVisits.some(visit => visit.id === id),
    )

    if (validVisitIds.length === 0) {
      handleError(
        'schedule.chain_deployment_info.visit_ids',
        'The selected visit does not exist in this study. Please select a valid visit for deployment.',
      )
    } else if (validVisitIds.length < visitIdsToValidate.length) {
      handleError(
        'schedule.chain_deployment_info.visit_ids',
        'Some of the selected visits do not exist in this study. Please review and update your selections.',
      )
    }
  }

  if (armIds.length > 1) {
    const deployVisits = collectDeployVisits(chain_deployment_info)
    const missingArmVisits = getArmsMissingExpireVisits(
      armIds,
      deployVisits,
      visit => getArmIdForVisitScheduleId(studyVisitSchedule, visit.visit_schedule_id),
    )
    if (missingArmVisits.length > 0) {
      handleError('schedule.chain_deployment_info.visit_ids', STRINGS.pleaseSelVisitFromEachArm)
    }
  }
}

/**
 * GOLD-11299: Validate chain expiry info for invalid arm/visit IDs
 * When instrument is configured to expire by visit, the arm and visits must exist in the study
 */
const _getChainExpiryErrors = (schedule, studyVisitSchedule, handleError) => {
  const { chain_expiry_info, chain_deployment_info, expire } = schedule
  if (!chain_expiry_info || !studyVisitSchedule || studyVisitSchedule.length === 0) return

  const { visit_ids, chain_expiry_by_ss_visit, expiry_condition } = chain_expiry_info

  if (!chain_expiry_by_ss_visit) return
  if (!expire?.expire_on_visit_confirmation) return

  const deployArmIds = getChainDeploymentStudyScheduleIds(chain_deployment_info)
  const expireArmIds = getChainDeploymentStudyScheduleIds(chain_expiry_info)
  const armIds = deployArmIds.length > 0 ? deployArmIds : expireArmIds

  if (armIds.length === 0) return

  const invalidArmIds = armIds.filter(armId => !studyVisitSchedule.some(arm => arm.id === armId))
  if (invalidArmIds.length > 0) {
    handleError(
      'schedule.chain_expiry_info.study_schedule_id',
      'The selected arm for expiration does not exist in this study. Please select a valid arm.',
    )
    return
  }

  const visitsForArm = armId => {
    const arm = studyVisitSchedule.find(item => item.id === armId)
    if (!arm?.phases) return []
    return arm.phases.flatMap(phase => {
      const fromTemplates = phase.visit_templates || []
      const fromCycles = (phase.cycles || []).flatMap(cycle =>
        (cycle.visits || []).map(visit => ({
          ...visit,
          visit_schedule_id: visit.visit_schedule_id || cycle.id,
        })),
      )
      return [...fromTemplates, ...fromCycles]
    })
  }

  const allVisits = armIds.flatMap(visitsForArm)
  const visitIdsToValidate =
    Array.isArray(visit_ids) && visit_ids.length > 0
      ? visit_ids
      : collectExpireVisits(chain_expiry_info).map(visit => visit.visit_id)

  if (visitIdsToValidate.length > 0) {
    const validVisitIds = visitIdsToValidate.filter(id => allVisits.some(visit => visit.id === id))
    if (validVisitIds.length === 0) {
      handleError(
        'schedule.chain_expiry_info.visit_ids',
        'None of the selected expiration visits exist in this study. Please select valid visits for expiration.',
      )
    } else if (validVisitIds.length < visitIdsToValidate.length) {
      handleError(
        'schedule.chain_expiry_info.visit_ids',
        'Some of the selected expiration visits do not exist in this study. Please review and update your selections.',
      )
    }
  }

  if (expiry_condition?.terms && Array.isArray(expiry_condition.terms) && expiry_condition.terms.length > 0) {
    const invalidTerms = expiry_condition.terms.filter(term => {
      const termArm = studyVisitSchedule.find(arm => arm.id === term.study_schedule_id)
      if (!termArm) return true
      return !visitsForArm(term.study_schedule_id).some(visit => visit.id === term.visit_id)
    })

    if (invalidTerms.length === expiry_condition.terms.length) {
      handleError(
        'schedule.chain_expiry_info.expiry_condition',
        'None of the configured expiration visits exist in this study. Please select valid visits for expiration.',
      )
    } else if (invalidTerms.length > 0) {
      handleError(
        'schedule.chain_expiry_info.expiry_condition',
        'Some of the configured expiration visits do not exist in this study. Please review and update your selections.',
      )
    }
  }

  const missingArmVisits = getArmsMissingExpireVisits(
    armIds,
    collectExpireVisits(chain_expiry_info),
    visit => getArmIdForVisitScheduleId(studyVisitSchedule, visit.visit_schedule_id),
  )
  if (missingArmVisits.length > 0) {
    handleError('schedule.chain_expiry_info.visit_ids', STRINGS.pleaseSelVisitFromEachArm)
  }
}

const _getDeployErrors = ({ deploy }, handleError) => {
  if (!deploy) {
    handleError('schedule.deploy', 'Missing deploy field.')
  } else {
    if (deploy.hasOwnProperty('first_login') && _isInvalid(deploy.first_login.interval)) {
      handleError('schedule.deploy.first_login.interval', 'Must specify a relative login interval')
    }
    if (deploy.hasOwnProperty('absolute') && fromUTCMidnightSeconds(deploy.absolute).isBefore(moment(), 'day')) {
      handleError('schedule.deploy.absolute', `Deploy date cannot be before today's date`)
    }
  }
}

const _getExpireErrors = ({ expire, deploy }, handleError) => {
  if (!expire) {
    handleError('expire', 'Missing expire field.')
  } else if (expire.hasOwnProperty('absolute')) {
    if (moment(fromUTCMidnightSeconds(expire.absolute)).isBefore(moment(), 'day')) {
      handleError('schedule.expire.absolute', `Expiration date cannot be before today's date`)
    } else if (
      deploy.absolute &&
      fromUTCMidnightSeconds(expire.absolute).isBefore(fromUTCMidnightSeconds(deploy.absolute))
    ) {
      handleError('schedule.expire.absolute', 'Expiration date cannot be before deploy date.')
    }
  } else if (expire.hasOwnProperty('first_login')) {
    // @TODO: Check interval_type ?
    if (_isInvalid(expire.first_login.interval)) {
      handleError('schedule.expire.first_login.interval', 'Must specify a relative expiration interval.')
    }
    if (expire.first_login.interval < deploy.first_login.interval) {
      handleError(
        'schedule.expire.first_login.interval',
        'Relative expiration time cannot be less than relative start time.',
      )
    }
  }
}

const _instrumentCohortValidation = (cohort, handleError) => {
  if (cohort.type === GROUP_TYPE_MAP.age) {
    if (_isInvalid(cohort.filter.min_age)) handleError('schedule.cohort.filter.min_age', 'Minimum age must be set')
    if (_isInvalid(cohort.filter.max_age)) handleError('schedule.cohort.filter.max_age', 'Maximum age must be set')
  } else if (cohort.type === GROUP_TYPE_MAP.cohort) {
    if (cohort.filter.include.length <= 0) {
      handleError('schedule.cohort.filter.include', 'You must assign at least one track')
    }
  } else if (cohort.type === GROUP_TYPE_MAP.sites) {
    if (cohort.filter.site_ids.length <= 0) {
      handleError('schedule.cohort.filter.site_ids', 'You must assign at least one site ')
    }
  }
}

const _getCohortErrors = ({ cohort }, handleError) => {
  if (!cohort) {
    handleError('expire', 'Missing cohort field.')
  } else if (Array.isArray(cohort)) {
    if (cohort.length === 0) handleError('schedule.cohort.filter.include', 'Must assign at least one')
    else {
      cohort.forEach(item => {
        _instrumentCohortValidation(item, handleError)
      })
    }
  } else if (Object.keys(cohort)?.length === 0) {
    handleError('schedule.cohort.filter.error', 'Must assign at least one')
  } else {
    _instrumentCohortValidation(cohort, handleError)
  }
}

const _getRecurringErrors = (schedule, handleError) => {
  if (schedule.recurring) {
    const recurringScheme = schedule.recurring.scheme
    if (isRepeatTimeUnitOpt(recurringScheme)) {
      const intervalErrors = getIntervalErrors(schedule.expire, schedule.deploy, schedule.recurring.interval)
      Object.keys(intervalErrors).forEach(key => {
        handleError(key, intervalErrors[key])
      })
      validateRecurringFields(recurringScheme).forEach(key => {
        if (!schedule.recurring.hasOwnProperty(key)) {
          handleError(`schedule.recurring.${key}`, `${key} cannot be empty.`)
        }
      })
    } else if (
      schedule.recurring.scheme !== 'persistent' &&
      schedule.recurring.scheme !== 'n-hours' &&
      schedule.recurring.scheme !== 'n-minutes' &&
      schedule.recurring.scheme !== 'n-months'
    ) {
      handleError('schedule.recurring.scheme', 'Invalid scheme type')
    }
  }
}

const _getConditionErrors = (conditions, handleError) => {
  const conditionErrors = {}
  conditions.forEach((condition, index) => {
    const { triggers } = condition
    const triggerType = triggers ? triggers[0].resource_function_name : null
    const resourceFunctionParams = triggers ? triggers[0].resource_function_params : {}
    switch (triggerType) {
      case TRIGGER_TYPE_MAP.singleScore:
      case TRIGGER_TYPE_MAP.score: {
        const scoreTriggerErrors = {}
        let hasErrors = false
        if (resourceFunctionParams.instrument_score === '') {
          hasErrors = true
          scoreTriggerErrors.score = 'Score cannot be empty'
        }
        if (hasErrors) conditionErrors[index] = scoreTriggerErrors
        break
      }
      case TRIGGER_TYPE_MAP.compliance: {
        const complianceTriggerErrors = {}
        let hasErrors = false
        if (resourceFunctionParams.window === '') {
          hasErrors = true
          complianceTriggerErrors.daysAfterDeployment = 'Days after deployment cannot be empty'
        }
        // if (resourceFunctionParams.site_ids.length === 0) {
        //   hasErrors = true
        //   complianceTriggerErrors.siteIds = 'Please select at least one site'
        // }
        if (resourceFunctionParams.completion_comparison === '') {
          hasErrors = true
          complianceTriggerErrors.completionComparison = 'Completion rate cannot be empty'
        }
        if (hasErrors) conditionErrors[index] = complianceTriggerErrors
        break
      }
      default:
        return null
    }
  })
  if (Object.keys(conditionErrors).length > 0) handleError('conditions', conditionErrors)
}

const getIntervalErrors = (expire, deploy, interval) => {
  let start
  let end
  const errors = {}
  // handle warning conditions
  if (!interval) {
    errors['schedule.recurring.interval'] = 'Recurring interval cannot be 0 or empty'
  }
  if (NEVER in expire) return errors
  if (RELATIVE_TO_FIRST_LOGIN in deploy) {
    // warn if recurring interval is greater than relative expiration interval
    if (expire.first_login && expire.first_login.interval - deploy.first_login.interval < interval) {
      errors['schedule.recurring.interval'] = 'Repeat interval cannot be greater than expiration interval.'
    }
  } else if (RELATIVE_TO_SEND_IDEAL in expire) {
    if (expire.relative_to_send_ideal.interval - 1 < interval) {
      errors['schedule.recurring.interval'] = 'Repeat interval cannot be greater than expiration interval.'
    }
  } else {
    if (NOW in deploy) {
      // The time when current day started in UTC
      start = currentDayStartInUTC()
    } else {
      start = fromUTCMidnightSeconds(deploy.absolute)
    }
    // warn if absolute expiration is before the first repeat
    if (expire.absolute) {
      end = fromUTCMidnightSeconds(expire.absolute)

      if (
        moment(start)
          .add(interval - 1, 'days')
          .isSameOrAfter(end, 'days')
      ) {
        errors['schedule.recurring.interval'] = 'Repeat interval exceeds the time of expiration.'
      }
    }
  }
  return errors
}

export const getFormulaErrors = ({ domains, scores }) => {
  const errors = {
    scores: {},
  }
  if (!domains || !scores) return {}
  const validDomainIds = Object.keys(domains)

  scores.forEach(score => {
    const { id, name, formula } = score
    const scoreErrors = {}

    if (name === '') {
      scoreErrors.emptyScoreNameErr = 'Please enter the score name'
    }
    const isInvalid = getDoesFormulaContainInvalidChars(formula, validDomainIds)
    const isIncomplete = getIsFormulaIncomplete(formula, validDomainIds)
    if (formula === '') {
      scoreErrors.emptyFormulaErr = 'Please enter a formula'
    }
    if (isInvalid) {
      scoreErrors.invalidCharFormulaErr = 'Please use existing Domains and supported operations'
    }
    if (isIncomplete) {
      scoreErrors.incompleteFormulaErr = 'Please complete this formula'
    }
    if (isIncomplete && isInvalid) {
      scoreErrors.invalidCharFormulaErr = `1. Please use existing Domains and supported operations`
      scoreErrors.incompleteFormulaErr = `2. Please complete this formula`
    }

    if (Object.keys(scoreErrors).length) {
      errors.scores[id] = scoreErrors
    }
  })
  if (!Object.keys(errors.scores).length) return {}
  return errors
}

export const getSigneeRequiredCount = signees => {
  return Object.values(signees).reduce((prev, curr) => prev + (curr.required & 1), 0)
}

export const checkForAdvancedDeploymentOptions = (_instrumentAdvancedOptions = {}) => {
  if (!_instrumentAdvancedOptions) return false
  return !!Object.keys(_instrumentAdvancedOptions).length
}

const VALIDATION_FIELD_LABELS = {
  visibilityRanges: 'Visibility times',
  instrument_roles: 'Clinician level',
  conditions: 'Trigger conditions',
  'schedule.platform_type': 'Platform',
  'schedule.cohort.filter.include': 'Tracks',
  'schedule.cohort.filter.site_ids': 'Sites',
  'schedule.cohort.filter.min_age': 'Minimum age',
  'schedule.cohort.filter.max_age': 'Maximum age',
  'schedule.cohort.filter.error': 'Audience',
  'schedule.chain_expiry_info.visit_ids': 'Expiration visit',
  'schedule.chain_expiry_info.study_schedule_id': 'Expiration arm',
  'schedule.chain_expiry_info.expiry_condition': 'Expiration visit',
  'schedule.chain_deployment_info.visit_ids': 'Deployment visit',
  'schedule.chain_deployment_info.visit_id': 'Deployment visit',
  'schedule.chain_deployment_info.study_schedule_id': 'Deployment arm',
  'schedule.chain_deployment_info.instrument_id': 'Trigger instrument',
  'schedule.recurring.interval': 'Repeat interval',
  'schedule.recurring.scheme': 'Repeat scheme',
  'schedule.recurring.visible_days': 'Visible days',
  'schedule.deploy.absolute': 'Start date',
  'schedule.deploy.first_login.interval': 'Start interval',
  'schedule.expire.absolute': 'Expiration date',
  'schedule.expire.first_login.interval': 'Expiration interval',
}

const MAX_VALIDATION_NOTY_MESSAGES = 8

const escapeHtml = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

export const humanizeValidationFieldKey = (key = '') => {
  const withoutIndexes = key.replace(/\[\d+\]/g, '')
  if (VALIDATION_FIELD_LABELS[key]) return VALIDATION_FIELD_LABELS[key]
  if (VALIDATION_FIELD_LABELS[withoutIndexes]) return VALIDATION_FIELD_LABELS[withoutIndexes]
  const topLevel = withoutIndexes.split('.')[0]
  if (VALIDATION_FIELD_LABELS[topLevel]) return VALIDATION_FIELD_LABELS[topLevel]
  return withoutIndexes
    .replace(/^schedule\./, '')
    .replace(/_/g, ' ')
    .replace(/\./g, ' / ')
    .replace(/^\w/, char => char.toUpperCase())
}

const formatFieldMessage = (key, message) => {
  const label = humanizeValidationFieldKey(key)
  if (!message) return label
  if (key.startsWith('questions.')) return message
  if (String(message).toLowerCase().includes(label.toLowerCase())) return message
  return `${label}: ${message}`
}

const nextErrorKey = (parentKey, childKey) =>
  /^\d+$/.test(childKey) ? `${parentKey}[${childKey}]` : `${parentKey}.${childKey}`

/**
 * Flatten nested instrument validation errors into user-facing messages.
 * Nested objects (e.g. trigger `conditions`) are walked so the toast/banner
 * can name the actual problem instead of a generic "fields marked in red".
 */
export const collectInstrumentValidationMessages = (errors = {}) => {
  const messages = []

  const walk = (key, value) => {
    if (value == null || value === '') {
      messages.push(formatFieldMessage(key, 'This field has a validation error'))
      return
    }
    if (typeof value === 'string') {
      messages.push(formatFieldMessage(key, value))
      return
    }
    if (typeof value === 'boolean' || typeof value === 'number') {
      messages.push(formatFieldMessage(key, String(value)))
      return
    }
    if (Array.isArray(value)) {
      value.forEach((item, index) => walk(`${key}[${index}]`, item))
      return
    }
    if (typeof value === 'object') {
      const nestedKeys = Object.keys(value)
      if (!nestedKeys.length) {
        messages.push(formatFieldMessage(key, 'This field has a validation error'))
        return
      }
      nestedKeys.forEach(childKey => walk(nextErrorKey(key, childKey), value[childKey]))
    }
  }

  Object.keys(errors).forEach(key => walk(key, errors[key]))
  return messages
}

export const formatInstrumentValidationNotyMessage = (errors = {}) => {
  const details = collectInstrumentValidationMessages(errors)
  if (!details.length) {
    return 'Please correct the required fields marked in red.'
  }
  const shown = details.slice(0, MAX_VALIDATION_NOTY_MESSAGES)
  const remaining = details.length - shown.length
  const list = shown.map(line => `• ${escapeHtml(line)}`).join('<br/>')
  const more = remaining > 0 ? `<br/>• and ${remaining} more` : ''
  return `Please correct the following:<br/>${list}${more}`
}

/** True when any error key equals or is nested under the given keys/prefixes. */
export const errorsMatchAnyKey = (errors = {}, keys = []) => {
  const errorKeys = Object.keys(errors || {})
  if (!errorKeys.length || !keys.length) return false
  return keys.some(key =>
    errorKeys.some(
      errorKey => errorKey === key || errorKey.startsWith(`${key}.`) || errorKey.startsWith(`${key}[`),
    ),
  )
}
